import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { BRAND_NAME } from '../shared/brand';
import './landing.css';

export default function LandingLayout({ children, maker = false, joinHref = '#join' }: { children?: ReactNode; maker?: boolean; joinHref?: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open) return;
    const links = menu.current?.querySelectorAll<HTMLAnchorElement>('a');
    links?.[0]?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') { setOpen(false); menuButton.current?.focus(); }
      if (event.key !== 'Tab' || !links?.length) return;
      if (event.shiftKey && document.activeElement === menuButton.current) {
        event.preventDefault(); links[links.length - 1].focus();
      } else if (!event.shiftKey && document.activeElement === links[links.length - 1]) {
        event.preventDefault(); menuButton.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  return <div className="hk">
    <a href="#main-content" className="hk-skip">Skip to content</a>
    <header className="hk-header">
      <a href="/" className="hk-wordmark" aria-label={`${BRAND_NAME} home`}>{BRAND_NAME}</a>
      <nav className="hk-desktop-nav" aria-label="Main navigation">
        <a href="/#approach">Our approach</a>
        <a href={maker ? '/' : '/sell'}>{maker ? 'For shoppers' : 'For makers'}</a>
        {maker && <a href="/tools/etsy-fee-audit" className="hk-fa-nav">Etsy fee audit</a>}
        <a href={joinHref} className="hk-button hk-header-cta">{maker ? 'Join the maker list' : 'Get a first look'}</a>
      </nav>
      <button type="button" className="hk-menu-button" ref={menuButton} onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="landing-navigation" aria-label={open ? 'Close menu' : 'Open menu'}>{open ? <X /> : <Menu />}</button>
      {open && <nav id="landing-navigation" ref={menu} className="hk-mobile-nav" aria-label="Mobile navigation">
        <a href="/#approach" onClick={() => setOpen(false)}>Our approach</a>
        <a href={maker ? '/' : '/sell'}>{maker ? 'For shoppers' : 'For makers'}</a>
        {maker && <a href="/tools/etsy-fee-audit">Etsy fee audit</a>}
        <a href={joinHref} onClick={() => setOpen(false)}>{maker ? 'Join the maker list' : 'Get a first look'}</a>
        <a href="/login">Log in</a>
      </nav>}
    </header>
    <main id="main-content">{children ?? <Outlet />}</main>
    <footer className="hk-footer">
      <div><a href="/" className="hk-wordmark">{BRAND_NAME}</a><p>Objects with meaning. People with a story.</p></div>
      <nav aria-label="Footer">
        <a href={maker ? '/' : '/sell'}>{maker ? 'For shoppers' : 'For makers'}</a>
        <a href="/#approach">Our approach</a>
        <a href="/interest/privacy">Privacy</a>
        <a href="/interest/manage">Manage your interest</a>
        <a href="/login">Log in</a>
      </nav>
      <div className="hk-footer-bottom"><span>© {new Date().getFullYear()} {BRAND_NAME}</span><a href="/seller/dashboard">Maker dashboard</a><a href="/guides/handmade-jewelry">The craft journal</a></div>
    </footer>
  </div>;
}
